import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert } from 'react-bootstrap';
import { getIndexerErrorInfo } from '../../api/errors';
import {
  useLazyGetProjectAdvancedStatsQuery,
  useLazyGetProjectInspectStatsQuery,
} from '../../api/indexerApi';
import type {
  ProjectAdvancedStatsData,
  ProjectInspectStatsData,
  ProjectJob,
} from '../../api/types';
import { segmentIndexChartData } from '../../lib/advanced-stats';
import {
  inspectDailyBarSeries,
  segmentIndexBarSeries,
} from '../../lib/chart-colors';
import { statsRangeHint, type StatsRange } from '../../lib/job-range';
import {
  aggregateInspectJobsByDay,
  canonicalTableRows,
  crawlFreshnessSlices,
  noindexDonutSlices,
  pctOf,
} from '../../lib/overview-stats';
import { formatFaNumber } from '../../lib/utils';
import { IndexerChartCard } from '../IndexerChartCard';
import { InspectChartsPreload } from '../preloads/IndexerPreloads';
import { CoverageBreakdownTable } from './CoverageBreakdownTable';
import { DonutChart } from './DonutChart';
import { HorizontalSegmentChart } from './HorizontalSegmentChart';
import { StackedBarChart } from './StackedBarChart';

/** Shared height so paired charts (and their legends) stay aligned. */
const INSPECT_PAIR_CHART_HEIGHT = 280;

export function InspectPageCharts({
  projectId,
  rangeDays,
  jobs,
}: {
  projectId: string;
  rangeDays: StatsRange;
  jobs: ProjectJob[];
}) {
  const [stats, setStats] = useState<ProjectInspectStatsData | null>(null);
  const [advanced, setAdvanced] = useState<ProjectAdvancedStatsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [getProjectInspectStats] = useLazyGetProjectInspectStatsQuery();
  const [getProjectAdvancedStats] = useLazyGetProjectAdvancedStatsQuery();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [inspectData, advancedData] = await Promise.all([
        getProjectInspectStats(projectId, true).unwrap(),
        getProjectAdvancedStats({ projectId, days: rangeDays }, true).unwrap(),
      ]);
      setStats(inspectData);
      setAdvanced(advancedData);
    } catch (err) {
      setError(
        getIndexerErrorInfo(err).message || 'بارگذاری نمودارها ناموفق بود',
      );
      setStats(null);
      setAdvanced(null);
    } finally {
      setLoading(false);
    }
  }, [projectId, rangeDays, getProjectInspectStats, getProjectAdvancedStats]);

  useEffect(() => {
    void load();
  }, [load]);

  const noindexSlices = useMemo(
    () => (stats ? noindexDonutSlices(stats.noindex) : []),
    [stats],
  );
  const canonicalRows = useMemo(
    () =>
      stats
        ? canonicalTableRows(
            stats.canonical,
            stats.inventory.total - stats.inventory.neverInspected,
          )
        : [],
    [stats],
  );
  const crawlSlices = useMemo(
    () => (stats ? crawlFreshnessSlices(stats.crawlFreshness) : []),
    [stats],
  );
  const segmentIndexData = useMemo(
    () => (advanced ? segmentIndexChartData(advanced.segmentsIndex) : []),
    [advanced],
  );
  const inspectDaily = useMemo(
    () => aggregateInspectJobsByDay(jobs, rangeDays),
    [jobs, rangeDays],
  );
  const rangeHint = statsRangeHint(rangeDays);

  const noindexPct = stats
    ? pctOf(
        stats.noindex.noindex,
        Math.max(1, stats.noindex.noindex + stats.noindex.indexingAllowed),
      )
    : 0;

  return (
    <div>
      {error && (
        <Alert variant="danger" className="mb-3" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && !stats ? (
        <InspectChartsPreload />
      ) : stats ? (
        <div className="d-flex flex-column gap-3">
          <div className="row g-3 align-items-stretch">
            <div className={advanced ? 'col-lg-6 min-w-0' : 'col-12 min-w-0'}>
              <IndexerChartCard
                className="overview-chart-card h-100 mb-0 d-flex flex-column"
                bodyClassName="flex-grow-1 d-flex flex-column"
                title="وضعیت ایندکس"
                subtitle={`نتیجهٔ URL Inspection در بازهٔ ${rangeHint}`}
                guideKey="inspect-daily-activity"
              >
                <div className="mt-auto w-100">
                  <StackedBarChart
                    data={inspectDaily}
                    series={inspectDailyBarSeries()}
                    height={INSPECT_PAIR_CHART_HEIGHT}
                  />
                </div>
              </IndexerChartCard>
            </div>
            {advanced && (
              <div className="col-lg-6 min-w-0">
                <IndexerChartCard
                  className="overview-chart-card h-100 mb-0 d-flex flex-column"
                  bodyClassName="flex-grow-1 d-flex flex-column"
                  title="وضعیت بر اساس مسیر"
                  subtitle="سگمنت اول مسیر URL"
                  guideKey="inspect-segment-index"
                >
                  <div className="mt-auto w-100">
                    <HorizontalSegmentChart
                      data={segmentIndexData}
                      series={segmentIndexBarSeries()}
                      height={INSPECT_PAIR_CHART_HEIGHT}
                      autoSize={false}
                    />
                  </div>
                </IndexerChartCard>
              </div>
            )}
          </div>

          <div className="row g-3">
            <div className="col-lg-4 col-md-6 min-w-0">
              <IndexerChartCard
                className="overview-chart-card h-100 mb-0"
                title="وضعیت Noindex"
                subtitle="فقط URLهای بازرسی‌شده"
                guideKey="inspect-noindex"
              >
                <DonutChart
                  slices={noindexSlices}
                  centerValue={
                    noindexSlices.length > 0
                      ? `${formatFaNumber(noindexPct)}٪`
                      : undefined
                  }
                  centerLabel="noindex"
                  height={220}
                />
              </IndexerChartCard>
            </div>
            <div className="col-lg-4 col-md-6 min-w-0">
              <IndexerChartCard
                className="overview-chart-card h-100 mb-0"
                title="تازگی کرول"
                subtitle="بر اساس lastCrawlTime"
                guideKey="inspect-crawl-freshness"
              >
                <DonutChart slices={crawlSlices} height={220} />
              </IndexerChartCard>
            </div>
            <div className="col-lg-4 min-w-0">
              <IndexerChartCard
                className="overview-chart-card h-100 mb-0"
                title="جدول Canonical"
                subtitle="تعداد و درصد هر حالت"
                guideKey="inspect-canonical-table"
              >
                <CoverageBreakdownTable
                  rows={canonicalRows}
                  total={Math.max(
                    0,
                    stats.inventory.total - stats.inventory.neverInspected,
                  )}
                />
              </IndexerChartCard>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
